import { Link, useLocation, useNavigate } from "react-router-dom";
import { Lock, LogOut, Menu, X } from "lucide-react";

import BrandLogo from "@/components/BrandLogo";
import ThemeToggle from "@/components/ThemeToggle";
import { useSidebarDrawer } from "@/components/SidebarDrawerContext";

import { endSuperAdminSession } from "./session";

const PAGE_TITLES: Record<string, string> = {
  "/super-admin/home": "Home",
  "/super-admin/orgs": "Organizations",
};

const prettifySegment = (segment: string) =>
  segment.split("-").map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(" ");

/**
 * Top bar for the super admin shell — the breadcrumb + actions bar that sits
 * above the page content, matching the admin/buyer shells. Self-contained for
 * the same reason as SuperAdminSidebar: the shared Navigation is driven by the
 * AuthContext user, which a super admin session doesn't have.
 */
export default function SuperAdminNavbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const drawer = useSidebarDrawer();

  const segments = location.pathname.split("/").filter(Boolean);
  const page = PAGE_TITLES[location.pathname] ?? prettifySegment(segments[segments.length - 1] ?? "Home");

  const handleSignOut = () => {
    endSuperAdminSession();
    navigate("/super-admin/sign-in", { replace: true });
  };

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur">
      <div className="px-4 sm:px-6 lg:px-8">
        <nav className="relative flex h-16 items-center gap-3">
          {/* Brand only below lg, where the sidebar (and its logo) is hidden. */}
          <div className="flex min-w-0 items-center gap-3 lg:hidden">
            <Link to="/super-admin/home" className="flex items-center">
              <BrandLogo className="h-10 w-auto" />
            </Link>
          </div>

          {/* Breadcrumb — nav itself lives in the sidebar. */}
          <div className="hidden min-w-0 flex-1 items-center gap-2 pl-2 text-sm lg:flex">
            <span className="whitespace-nowrap text-muted-foreground">Super Admin</span>
            <span className="text-muted-foreground/40">/</span>
            <span className="truncate font-semibold text-primary">{page}</span>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <span className="hidden items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-[0.6rem] font-bold uppercase tracking-[0.14em] text-primary sm:inline-flex">
              <Lock className="h-3.5 w-3.5" />
              Restricted
            </span>
            <ThemeToggle />
            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex items-center gap-2 rounded-full border border-border/80 bg-card px-3 py-1.5 text-sm font-semibold text-rose-600 transition-colors duration-200 hover:border-rose-500/40 hover:bg-rose-500/10"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
            {/* Below `lg` the sidebar is hidden, so this is the only way into
                the rest of the console. */}
            {drawer && (
              <button
                type="button"
                onClick={drawer.toggle}
                aria-label={drawer.open ? "Close navigation" : "Open navigation"}
                aria-expanded={drawer.open}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border/80 bg-card text-foreground transition-colors duration-200 hover:bg-accent lg:hidden"
              >
                {drawer.open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}
